import { ShiftTemplatePayload } from '@app/reducers/orm/shift/shift.model';
import { TimesheetStatus } from '@app/reducers/orm/timesheet/timesheet.model';
import { getRandomDefaultColor, toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { addDays, subDays } from 'date-fns';

import type {
  ScheduledShiftPayload,
  ShiftAssignment,
  TeamShiftOption,
  TimesheetEntryPayload,
  TimesheetSlot,
} from './dev-tools-hydration.models';
import {
  generateUniqueEmail,
  getRandomElement,
  getRandomIntBetween,
  MOCK_CITIES,
  MOCK_DEPARTMENTS,
  MOCK_FIRST_NAMES,
  MOCK_JOB_TITLES,
  MOCK_LAST_NAMES,
  MOCK_LOCATION_SUFFIXES,
  MOCK_PHONE_NUMBER,
  MOCK_SHIFT_TEMPLATES,
  MOCK_SHIFT_TIMES,
  MOCK_TIMESHEET_NOTES,
  randomWeightedValue,
  shuffleArray,
} from './dev-tools-mock-data';

/** Scheduled shifts are generated in the coming 4 weeks, timesheets in the past 4 weeks. */
const ACTIVITY_WINDOW_DAYS = 28;

/** Scheduled shifts are spread over this many batched roster requests when possible. */
const MAX_SCHEDULED_SHIFT_REQUESTS = 10;

export class DevToolsDataGenerator {
  private static generatedEmails = new Set<string>();

  static generateLocation() {
    return {
      name: `${getRandomElement(MOCK_CITIES)} ${getRandomElement(MOCK_LOCATION_SUFFIXES)}`,
      order: '0',
    };
  }

  static generateDepartment(locationId: string) {
    return {
      name: getRandomElement(MOCK_DEPARTMENTS),
      color: getRandomDefaultColor(),
      location_id: locationId,
    };
  }

  static generateDepartments(locationId: string, count: number) {
    return Array.from({ length: count }, () => this.generateDepartment(locationId));
  }

  static generateTeam(departmentId: string) {
    return {
      department_id: departmentId,
      name: `${getRandomElement(MOCK_JOB_TITLES)}s`,
      color: getRandomDefaultColor(),
    };
  }

  static generateTeams(departmentId: string, count: number) {
    return Array.from({ length: count }, () => this.generateTeam(departmentId));
  }

  static generateEmployee(teamId: string) {
    const firstName = getRandomElement(MOCK_FIRST_NAMES);
    const lastName = getRandomElement(MOCK_LAST_NAMES);
    const email = generateUniqueEmail(firstName, lastName, this.generatedEmails);

    // Track this email to ensure uniqueness
    this.generatedEmails.add(email);

    // Distribute employees across teams
    return {
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone_nr: MOCK_PHONE_NUMBER,
      function: getRandomElement(MOCK_JOB_TITLES),
      team_id: teamId,
      // The backend starts the auto-created contract on this date — half a year
      // to a year back, so timesheets can be generated for the past right away
      startdate: toISODateString(subDays(new Date(), getRandomIntBetween(182, 365))),
    };
  }

  static generateEmployees(teamIds: string[], count: number, existingEmails: string[] = []) {
    // Initialize with existing emails to prevent conflicts
    this.initializeEmailTracking(existingEmails);

    return Array.from({ length: count }, () =>
      this.generateEmployee(teamIds[Math.floor(Math.random() * teamIds.length)]),
    );
  }

  static generateShiftTemplate(departmentId: string): ShiftTemplatePayload {
    const template = getRandomElement(MOCK_SHIFT_TEMPLATES);

    return {
      id: '',
      rate_card_id: null,
      meals: '0',
      department_id: departmentId,
      long_name: template.long_name,
      name: template.name,
      description: `${template.long_name} between ${template.starttime.slice(0, 5)}h - ${template.endtime.slice(0, 5)}h`,
      starttime: template.starttime,
      endtime: template.endtime,
      hide_end_time: false,
      is_task: false,
      break: '30',
      paid_break: '30',
      order: '10',
      color: getRandomDefaultColor(),
    };
  }

  static generateShiftTemplates(departmentId: string, count: number) {
    return Array.from({ length: count }, () => this.generateShiftTemplate(departmentId));
  }

  /**
   * Builds roster payloads in big batches: the count is split over at most
   * MAX_SCHEDULED_SHIFT_REQUESTS requests (more only when there are too few
   * employees, as one request cannot schedule the same employee twice). Each
   * batch picks a random team, a random shift template of its department and a
   * batch of employees on its own date — dates stay distinct while the 4-week
   * window allows, so employees aren't scheduled twice on the same day.
   */
  static generateScheduledShifts(
    userIds: string[],
    teamShiftOptions: TeamShiftOption[],
    count: number,
  ): ScheduledShiftPayload[] {
    if (count <= 0 || userIds.length === 0 || teamShiftOptions.length === 0) {
      return [];
    }

    const batchCount = Math.max(Math.min(count, MAX_SCHEDULED_SHIFT_REQUESTS), Math.ceil(count / userIds.length));

    // The first batch always lands on today (the view checked right after
    // generating); the remaining batches sample the coming 4 weeks randomly
    const [todayDate, ...upcomingDates] = Array.from({ length: ACTIVITY_WINDOW_DAYS }, (unusedValue, dayOffset) =>
      toISODateString(addDays(new Date(), dayOffset)),
    );
    const futureDates = [todayDate, ...shuffleArray(upcomingDates)];

    let remaining = count;

    return Array.from({ length: batchCount }, (unusedValue, batchIndex) => {
      const batchSize = Math.ceil(remaining / (batchCount - batchIndex));
      remaining -= batchSize;

      const teamShiftOption = getRandomElement(teamShiftOptions);
      const shiftTimes = getRandomElement(MOCK_SHIFT_TIMES);

      return {
        id: '',
        date: futureDates[batchIndex % futureDates.length],
        department_id: teamShiftOption.departmentId,
        team_id: teamShiftOption.teamId,
        shift_id: getRandomElement(teamShiftOption.shiftIds),
        user_id: shuffleArray(userIds).slice(0, batchSize),
        starttime: shiftTimes.starttime,
        endtime: shiftTimes.endtime,
        hide_end_time: false,
        break: '30',
        paid_break: '0',
        description: '',
      };
    });
  }

  /**
   * Picks unique (employee, date) slots in the past weeks. Capped at the number
   * of available combinations so no employee gets two timesheets on one day.
   * When `contractedDatesByUser` is given, only dates the employee has a contract
   * on are used — the backend rejects timesheets outside a contract. Without it
   * (contracts unknown), all dates are candidates.
   */
  static generateTimesheetSlots(
    assignments: ShiftAssignment[],
    count: number,
    contractedDatesByUser?: Map<string, Set<string>>,
  ): TimesheetSlot[] {
    const candidates = assignments.flatMap((assignment) =>
      Array.from({ length: ACTIVITY_WINDOW_DAYS }, (unusedValue, dayOffset) => ({
        assignment,
        date: toISODateString(subDays(new Date(), dayOffset + 1)),
      })).filter(
        (candidate) =>
          !contractedDatesByUser || (contractedDatesByUser.get(assignment.userId)?.has(candidate.date) ?? false),
      ),
    );

    return shuffleArray(candidates).slice(0, count);
  }

  static generateTimesheet(slot: TimesheetSlot): TimesheetEntryPayload {
    const shiftTimes = getRandomElement(MOCK_SHIFT_TIMES);

    return {
      user_id: slot.assignment.userId,
      team_id: slot.assignment.teamId,
      department_id: slot.assignment.departmentId,
      shift_id: slot.assignment.shiftId,
      date: slot.date,
      starttime: this.#applyMinuteJitter(shiftTimes.starttime, 15),
      endtime: this.#applyMinuteJitter(shiftTimes.endtime, 15),
      clock: false,
      break: 30,
      paid_break: 0,
      status: randomWeightedValue([
        { value: TimesheetStatus.APPROVED, weight: 70 },
        { value: TimesheetStatus.PENDING, weight: 25 },
        { value: TimesheetStatus.DECLINED, weight: 5 },
      ]),
      note: getRandomElement(MOCK_TIMESHEET_NOTES),
    };
  }

  /**
   * Shifts a HH:mm:ss time by a random amount of minutes within ±jitterRange,
   * clamped to the same day, so generated timesheets don't all look identical.
   */
  static #applyMinuteJitter(time: string, jitterRange: number): string {
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + getRandomIntBetween(-jitterRange, jitterRange);
    const clampedMinutes = Math.min(Math.max(totalMinutes, 0), 23 * 60 + 59);
    const jitteredHours = String(Math.floor(clampedMinutes / 60)).padStart(2, '0');
    const jitteredMinutes = String(clampedMinutes % 60).padStart(2, '0');

    return `${jitteredHours}:${jitteredMinutes}:00`;
  }

  /**
   * Initialize email tracking with existing emails from the system
   */
  static initializeEmailTracking(existingEmails: string[]) {
    this.generatedEmails.clear();
    existingEmails.forEach((email) => this.generatedEmails.add(email));
  }

  /**
   * Reset the generated emails set (useful for testing or starting fresh)
   */
  static resetEmailTracking() {
    this.generatedEmails.clear();
  }
}
